import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorService } from '../../services/vendorService';
import type { Vendor, VendorFormData } from '../../types/vendor';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'linear-gradient(135deg, #d97706, #b45309)',
  border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#1c2033', cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'none',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
};

interface VendorFormProps {
  vendor?: Vendor;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const VendorForm: React.FC<VendorFormProps> = ({
  vendor,
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!vendor;

  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    paymentTerms: undefined,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof VendorFormData, string>>>({});

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name,
        email: vendor.email || '',
        phone: vendor.phone || '',
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        postalCode: vendor.postalCode || '',
        country: vendor.country || '',
        taxId: vendor.taxId || '',
        paymentTerms: vendor.paymentTerms || undefined,
      });
    }
  }, [vendor]);

  const createMutation = useMutation({
    mutationFn: vendorService.createVendor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-stats'] });
      onSuccess?.();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<VendorFormData> }) =>
      vendorService.updateVendor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-stats'] });
      if (vendor) {
        queryClient.invalidateQueries({ queryKey: ['vendor', vendor.id] });
      }
      onSuccess?.();
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof VendorFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof VendorFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Vendor name is required';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing && vendor) {
        await updateMutation.mutateAsync({ id: vendor.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
    } catch (error: any) {
      console.error('Error saving vendor:', error);
      if (error.response?.data?.message) {
        setErrors({ name: error.response.data.message });
      }
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 8, padding: '28px 32px', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
          {isEditing ? 'Edit Vendor' : 'Add New Vendor'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Section: Basic Information */}
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
            Basic Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Vendor Name — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="name" style={labelStyle}>Vendor Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={{ ...inputStyle, border: errors.name ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)' }}
                placeholder="Enter vendor name"
              />
              {errors.name && (
                <p style={{ margin: '6px 0 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" style={labelStyle}>Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{ ...inputStyle, border: errors.email ? '1px solid rgba(201,90,90,0.4)' : '1px solid rgba(92,78,51,0.25)' }}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p style={{ margin: '6px 0 0 0', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" style={labelStyle}>Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter phone number"
              />
            </div>

            {/* Tax ID */}
            <div>
              <label htmlFor="taxId" style={labelStyle}>Tax ID</label>
              <input
                type="text"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter tax ID"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <label htmlFor="paymentTerms" style={labelStyle}>Payment Terms (Days)</label>
              <input
                type="number"
                id="paymentTerms"
                name="paymentTerms"
                value={formData.paymentTerms || ''}
                onChange={handleChange}
                min="0"
                step="1"
                style={inputStyle}
                placeholder="30"
              />
            </div>
          </div>
        </div>

        {/* Section: Address */}
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
            Address
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Street Address — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="address" style={labelStyle}>Street Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter street address"
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" style={labelStyle}>City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter city"
              />
            </div>

            {/* State */}
            <div>
              <label htmlFor="state" style={labelStyle}>State / Province</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter state or province"
              />
            </div>

            {/* Postal Code */}
            <div>
              <label htmlFor="postalCode" style={labelStyle}>ZIP / Postal Code</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter ZIP or postal code"
              />
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" style={labelStyle}>Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter country"
              />
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 20, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
          <button type="button" onClick={onCancel} style={ghostBtn}>
            Cancel
          </button>
          <button type="submit" disabled={isLoading} style={{ ...primaryBtn, opacity: isLoading ? 0.7 : 1 }}>
            {isLoading ? 'Saving...' : (isEditing ? 'Update Vendor' : 'Create Vendor')}
          </button>
        </div>
      </form>
    </div>
  );
};
